// Optional sync between localStorage and the server's per-user setups
// table — never required for the app to work (see auth.js), and never
// runs unless the caller is logged in via useAuth(). Kept separate from
// both setups.js (owns the setup index) and state.js (owns the live
// active-setup data) since this is a third, purely network-orchestration
// concern that shouldn't teach either of those about fetch/auth.
//
// The DB is treated as the source of truth once logged in: sync runs
// automatically on page load and whenever the browser regains connectivity
// (see account_widget.vue's onMounted/online handler), not just on login
// and the manual "Sync now" button — which itself only shows up when
// there's a local change to push (see hasLocalChanges below); a change
// made on another device is picked up automatically, so the fix for that
// is just reloading the page, not this button. Deletions propagate too —
// deleting a setup on one device deletes it everywhere else on their next
// sync — via knownServerState, a persisted { [id]: updated_at } record of
// what this browser last saw on the server: anything whose id drops out of
// the server's list between syncs is treated as deleted, and anything this
// device deletes locally is queued (pendingDeletes, also persisted so it
// survives being offline across a reload) and pushed as soon as a sync can
// reach the server. Still whole-setup last-write-wins for edits, not a
// merge: if both sides changed the same setup since the last sync, one of
// the two edits is lost.
//
// The one exception to all of the above is the login/register transition
// itself (see loadFreshAfterLogin below): whatever this browser had
// locally right before authenticating is never merged into, or pushed
// onto, the account just logged into — it's discarded, and the account's
// own setups replace it outright. Regular syncAll()'s pull-then-push only
// takes over from the next sync onward, once the local and server state
// are known to belong to the same identity.
//
// Beyond page-load/reconnect/button, a logged-in session also auto-syncs
// after any local edit. Only raw sight_settings/marks typing goes through a
// debounce (see scheduleAutoSync below) — 100ms of quiet, just enough to
// coalesce a fast typist's individual keystrokes into one request, not a
// deliberate delay. Every other trigger (a finished compute, a rename,
// create, or delete, logging out) is a single discrete action with nothing
// else coming right behind it, so each pushes immediately via syncNow()
// instead of waiting out that debounce. This all reuses syncAll() itself,
// so every path is automatically subject to the same guards (logged-in
// check, first-sync ordering) and never throws back to a watcher or blocks
// editing, no matter how it goes.
//
// Every one of those triggers — the debounce, page load, reconnect, a
// delete's immediate push, the best-effort sync before logging out — is
// automated, not something the user directly asked for right now, so none
// of them ever produce a toast: syncAll()'s `silent` param (true by
// default) suppresses feedback for both success and failure alike. Only an
// explicit action gets to report its own outcome — the "Sync now" button
// (see account_widget.vue's onSync) passes `silent: false` and shows
// "Sync complete."/a failure message every time it's pressed, and
// loadFreshAfterLogin (login/register) reports its own failure the same
// way when called directly rather than via syncAll's fallback below.

import { computed, ref, watch } from 'vue';
import { useSetups, onSetupDeleted, onSetupChanged } from './setups.js';
import { useAuth } from './auth.js';
import { useShared, exportSetupJson, applySetupData, backfillTimestamps, getSetupCalc, waitForComputeIdle, onComputeFinished } from './state.js';
import { getServerUrl } from './server_url.js';
import { usePersisted } from './use_persisted.js';
import { useToasts } from './use_toasts.js';

const { pushToast } = useToasts();

// { [setupId]: updated_at } this browser last confirmed the server has, for
// the currently-logged-in account — used both to tell "deleted on the
// server since last time" apart from "created locally, never pushed yet"
// (pullAll), and to tell "has local edits since last sync" for the
// "Sync now" button's visibility (hasLocalChanges below). Cleared on
// logout (see resetSyncState) so switching accounts on one browser can't
// misread the new account's setups against the old account's history.
const knownServerState = usePersisted('sync-known-server-state', {});
// Ids queued for a server-side DELETE — populated by the onSetupDeleted
// hook below, drained by flushPendingDeletes. Persisted so a deletion made
// while offline still reaches the server once a sync succeeds, even across
// a page reload in between.
const pendingDeletes = usePersisted('sync-pending-deletes', []);
// Separate from knownServerState being non-empty: a brand new account with
// nothing saved yet legitimately pulls zero setups on its first sync, which
// would leave knownServerState looking exactly like "never synced" forever
// (see syncAll's guard) — this flag is the actual "has loadFreshAfterLogin
// run for the current login" marker, set regardless of how many setups
// came back.
const hasCompletedFirstSync = usePersisted('sync-first-sync-done', false);
// Whether the most recent sync attempt (of any kind — pull, push, or a
// queued delete) succeeded — tracked here so it survives a reload, even
// though nothing currently gates a toast on it (see reportFailure below;
// every automated trigger is silent regardless of this flag's value).
const lastSyncOk = usePersisted('sync-last-ok', true);

// Call this from every network-failure site in this file instead of
// pushToast directly — `silent` (threaded down from whatever triggered the
// sync; see the file header) is what decides whether the user actually
// sees it, not this flag's history.
function reportFailure(message, silent) {
  lastSyncOk.value = false;
  if (!silent) pushToast(message);
}

// Debounced auto-sync on every keystroke-level edit (see the file header).
// 100ms of quiet is enough to coalesce a fast typist's individual
// keystrokes into one request without needing to wait long enough to
// *feel* like a delay — sight_settings/marks fields are the one thing that
// still fires this rapidly; every other trigger (compute finishing,
// rename/create/delete, logout) goes straight to syncNow() below instead.
//
// Deliberately watches sight_settings/marks only, not all of `inputs` —
// description is edited freely without pushing anything until the user
// hits the Setup Description panel's own Save button (which calls syncAll
// directly), not on every keystroke like these two fields.
const AUTO_SYNC_DEBOUNCE_MS = 100;
let autoSyncTimer = null;

function scheduleAutoSync() {
  const { isLoggedIn } = useAuth();
  if (!isLoggedIn.value) return; // skip the timer entirely for the common (guest) case
  clearTimeout(autoSyncTimer);
  autoSyncTimer = setTimeout(syncAll, AUTO_SYNC_DEBOUNCE_MS);
}

const { inputs: autoSyncInputs } = useShared();
watch([() => autoSyncInputs.sight_settings, () => autoSyncInputs.marks], scheduleAutoSync, { deep: true });

// Cancels any pending debounced sync and runs one right now instead —
// used for discrete, one-off actions (a delete, a rename/create, a finished
// compute) that have nothing more coming right behind them, unlike a burst
// of keystrokes. Automated, so silent (the default) either way.
function syncNow() {
  clearTimeout(autoSyncTimer);
  syncAll();
}

onSetupDeleted((id) => {
  const { isLoggedIn } = useAuth();
  if (!isLoggedIn.value) return;
  if (!pendingDeletes.value.includes(id)) pendingDeletes.value.push(id);
  // Best-effort immediate push rather than waiting for the next scheduled
  // sync (page load / regained connectivity / manual button) — a delete is
  // the one edit users most want to see everywhere right away. Falls back
  // fine if this fails (offline, etc.): it's still queued above, and the
  // next successful sync picks it up.
  syncNow();
});

// A create or rename — see setups.js's onSetupChanged — is as discrete an
// action as a delete, so it gets the same immediate push rather than
// waiting out the typing-edit debounce above.
onSetupChanged(() => syncNow());

// A compute finishing (see state.js's onComputeFinished) is likewise a
// single discrete event, not a burst — push it immediately rather than
// making it wait out the same debounce as a still-in-progress typing edit.
onComputeFinished(() => syncNow());

export function resetSyncState() {
  clearTimeout(autoSyncTimer); // a pending auto-sync belongs to whatever identity is being left behind
  knownServerState.value = {};
  pendingDeletes.value = [];
  hasCompletedFirstSync.value = false;
  lastSyncOk.value = true;
}

// A setup's own last-write-wins timestamp (state.js's setupUpdatedAt,
// bumped by touchActiveSetup/setups.js's renameSetup) travels inside
// exportSetupJson's own output now, alongside its name — see that
// function's comment. This
// used to be derived here instead, by scanning sight_settings' and every
// mark's own updated_at (which exist purely for the user's own "updated 3m
// ago" labels, not for sync) and separately max'd against a setup-name
// timestamp; one clear field replaces both. `backfillTimestamps` is still
// called before a push so a setup that's never been made active locally
// (every per-field timestamp still null) doesn't ship nulls to the server —
// that's just display hygiene for whoever pulls it next, unrelated to the
// timestamp used for the conflict check itself.

// No AbortController/timeout here used to mean a request to an unreachable
// or hung backend (a dead dev-server proxy target, a stalled connection —
// unlike a plain connection refusal, which fetch rejects almost instantly)
// could sit unresolved indefinitely. Every sync attempt is already
// fire-and-forget from the caller's side (see the file header), so nothing
// upstream was literally frozen by this, but a sync that never resolves
// also never reports failure or gets a chance to retry — capping it here
// means the worst case is "one attempt takes up to 10s then fails/retries
// on the next trigger," not "silently stuck forever."
const API_FETCH_TIMEOUT_MS = 10_000;

// Exported for setup_log.js — the setup-history log is a separate,
// server-only concern (see its own header comment) but wants the same
// auth-header/timeout/401-handling this already does, rather than a second
// copy of it.
export async function apiFetch(path, options = {}) {
  const { authHeader, logout } = useAuth();
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), API_FETCH_TIMEOUT_MS);
  let res;
  try {
    res = await fetch(`${getServerUrl()}${path}`, {
      ...options,
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json', ...authHeader(), ...(options.headers || {}) },
    });
  } catch (err) {
    if (err.name === 'AbortError') {
      throw new Error(`Request to ${path} timed out.`);
    }
    throw err;
  } finally {
    clearTimeout(timeout);
  }
  if (res.status === 401) {
    logout();
    resetSyncState();
    // Same reasoning as account_widget.vue's onLogout: a 401 means this
    // browser's session belongs to an identity that no longer checks out
    // (expired token, or the account itself is gone — e.g. the server's DB
    // was reset), so whatever setups are cached locally belong to that
    // stale identity too and must not survive into whatever logs in next.
    // Without this, registering a brand-new account right after a 401
    // would hand it someone else's (or a deleted account's) old setups via
    // registerAndKeepLocal, which just pushes setups.value[0] as-is.
    const { replaceAllSetups } = useSetups();
    replaceAllSetups([], null);
    throw new Error('Session expired — please log in again.');
  }
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body.error || `Request to ${path} failed.`);
    err.status = res.status;
    throw err;
  }
  return body;
}

// Pushes every queued deletion. A 404 means the server already agrees it's
// gone (either it beat us to it, or it never made it there in the first
// place) — treated as success rather than an error. Returns whether every
// deletion in the queue is now confirmed gone (used by syncAll to decide
// whether this run counts as fully clean).
async function flushPendingDeletes(silent) {
  if (!pendingDeletes.value.length) return true;
  let allOk = true;
  const stillPending = [];
  for (const id of pendingDeletes.value) {
    try {
      await apiFetch(`/setups/${encodeURIComponent(id)}`, { method: 'DELETE' });
    } catch (err) {
      if (err.status === 404) continue;
      stillPending.push(id);
      allOk = false;
      reportFailure(`Could not delete a setup on the server yet: ${err.message}`, silent);
    }
  }
  pendingDeletes.value = stillPending;
  return allOk;
}

async function pullAll() {
  const { setups, deleteSetup } = useSetups();
  const { setups: remoteSetups } = await apiFetch('/setups');
  const remoteIds = new Set(remoteSetups.map((s) => s.id));

  // Anything we used to see on the server (as of our last successful sync)
  // but no longer do was deleted — by this device or another one logged
  // into the same account — so remove it here too. `silent: true` since
  // this deletion is already reflected server-side; no need to queue
  // another DELETE for it. deleteSetup itself keeps at least one setup
  // around, same guard as a local delete.
  for (const id of Object.keys(knownServerState.value)) {
    if (remoteIds.has(id)) continue;
    deleteSetup(id, { silent: true });
  }

  for (const remote of remoteSetups) {
    // Don't let a pull resurrect something this device just deleted but
    // hasn't successfully told the server about yet.
    if (pendingDeletes.value.includes(remote.id)) continue;

    const isNew = !setups.value.some((s) => s.id === remote.id);
    if (isNew) {
      // applySetupData registers a never-before-seen id on its own (it
      // upserts into the store rather than requiring the id to already
      // exist) without switching the active setup — a background pull
      // shouldn't yank the screen away from whatever the user is currently
      // looking at. `calc` carries over whatever fit the sending device had
      // already computed, so this setup doesn't need its own recompute the
      // first time it's switched to locally.
      applySetupData(remote.id, { ...remote.data, name: remote.name, updated_at: remote.updated_at, calc: remote.data.calc ?? null });
      continue;
    }

    const localData = JSON.parse(exportSetupJson(remote.id));
    if (remote.updated_at > (localData.updated_at || 0)) {
      applySetupData(remote.id, { ...remote.data, name: remote.name, updated_at: remote.updated_at, calc: remote.data.calc ?? null });
    }
  }

  return remoteSetups;
}

// Returns the server's own confirmed updated_at for this setup after the
// write — not just the value we sent — since the server may have kept an
// even newer value of its own instead of overwriting it (a `written:false`
// response, e.g. a race with another device's sync landing moments
// earlier); recording our own stale guess in that case would make
// hasLocalChanges wrongly think this setup still has unpushed local edits.
async function pushSetup(setup) {
  const { name, updated_at, ...data } = JSON.parse(exportSetupJson(setup.id));
  backfillTimestamps(data);
  // Riding along inside `data` (the server's one opaque JSON blob per
  // setup — see server/db.js) rather than folded into exportSetupJson
  // itself: this is a sync-only add-on, not part of the file export
  // format, so it comes from getSetupCalc instead (see its own comment).
  data.calc = getSetupCalc(setup.id);
  const body = await apiFetch(`/setups/${encodeURIComponent(setup.id)}`, {
    method: 'PUT',
    body: JSON.stringify({ name, data, updated_at: updated_at || 0 }),
  });
  return body.updated_at;
}

// Deletes first (so a delete this device already knows about never gets
// re-pushed by the step below), then pulls (so a setup that's newer on
// another device — or deleted there — is reconciled before this device's
// push would clobber or resurrect it), then pushes every remaining local
// setup. knownServerState is refreshed at the end to whatever we've just
// confirmed the server has, so the next sync's deletion diff (and
// hasLocalChanges' dirty check) are both accurate.
// Returns whether this run fully succeeded — callers that give explicit
// feedback for a manual trigger (see account_widget.vue's onSync) need this
// since syncAll deliberately never throws: every failure here is caught and
// reported via reportFailure internally rather than propagated, so
// `await syncAll()` alone can't tell a caller whether anything actually
// worked. `silent` defaults to true (every automated trigger — the
// debounced auto-sync, page load, reconnect, a delete's immediate push,
// the pre-logout attempt — calls this bare); only the "Sync now" button
// passes `{ silent: false }`.
//
// This is the actual work; exported `syncAll` below only decides whether to
// start a new run of this or join one already in flight.
async function runSyncAll(silent) {
  const { isLoggedIn } = useAuth();
  if (!isLoggedIn.value) return true;

  // loadFreshAfterLogin hasn't run yet for whoever is logged in right now —
  // e.g. the very first sync ever on a fresh browser/profile, or resuming
  // an already-valid token after a resetSyncState (logout, a 401, deleting
  // the account) without going through the login form itself. setups.js
  // always has at least one local setup at this point (its own bootstrap
  // "Default", freshly created with blank values and null timestamps if
  // this is genuinely a first run) — treating that as a real local edit and
  // pushing it would create a spurious duplicate "Default" on the server,
  // and backfillTimestamps would stamp it "just now" on every such load
  // since it's never actually been touched. Defer to the same hard replace
  // loadFreshAfterLogin uses instead of merging: nothing local can be
  // meaningfully reconciled with the server yet, so don't try. (Not simply
  // "knownServerState is empty": a brand new account with nothing saved
  // yet legitimately pulls zero setups on its real first sync, which would
  // leave knownServerState looking exactly like "never synced" forever and
  // permanently block this device from ever pushing its first setup.)
  if (!hasCompletedFirstSync.value) {
    return await loadFreshAfterLogin(silent);
  }

  // A compute can take multiple real seconds for some inputs — wait for it
  // to settle before reading anything to push, so a sync triggered mid-
  // compute (logout, the debounced auto-sync, "Sync now") doesn't push the
  // active setup's still-stale pre-compute value (see waitForComputeIdle's
  // own comment).
  await waitForComputeIdle();

  let ranClean = true;

  if (!(await flushPendingDeletes(silent))) ranClean = false;

  let remoteSetups;
  try {
    remoteSetups = await pullAll();
  } catch (err) {
    reportFailure(`Sync (pull) failed: ${err.message}`, silent);
    return false;
  }

  const { setups } = useSetups();
  const nowKnownState = {};
  for (const remote of remoteSetups) nowKnownState[remote.id] = remote.updated_at;
  for (const setup of setups.value) {
    if (!isLoggedIn.value) break; // a 401 above already logged out — stop instead of repeating it per setup
    try {
      const updatedAt = await pushSetup(setup);
      nowKnownState[setup.id] = updatedAt;
    } catch (err) {
      ranClean = false;
      reportFailure(`Sync failed for "${setup.name}": ${err.message}`, silent);
    }
  }
  knownServerState.value = nowKnownState;

  if (ranClean) lastSyncOk.value = true;
  return ranClean;
}

// Coalesces overlapping SILENT/automated triggers — page-mount sync, the
// debounced auto-sync, a finished compute, onLogout's pre-sync, and
// syncAll's own !hasCompletedFirstSync fallback into loadFreshAfterLogin —
// into a single in-flight run rather than letting each one launch its own
// full pull-then-push-every-setup cycle. Without this, a slow pull (a flaky
// connection, a cold server) makes several of those triggers' round trips
// stack up strictly sequentially instead of overlapping, since none of them
// previously waited to see if a run was already underway — a single
// login/logout could end up serializing several multi-second cycles back to
// back. A non-silent call (the "Sync now" button; loadFreshAfterLogin's
// direct callers) never joins a silent run's outcome — that run already
// decided its own reportFailure/toast behavior using its own `silent`
// value, so joining it would either swallow or misattribute this call's
// feedback. It instead waits for any in-flight silent run to clear, then
// always executes its own dedicated pass.
let inFlightSilentSync = null;
// Mirrors "a silent run is currently in flight" into a reactive ref —
// `inFlightSilentSync` itself is a plain variable holding a Promise, which
// the UI can't watch. Distinct from `!hasCompletedFirstSync` alone: that
// flag stays false for as long as loadFreshAfterLogin hasn't succeeded,
// including *after* a failed attempt has already given up (a 10s fetch
// timeout, offline, a 500) — combining it with this flag is what tells "the
// post-login pull is actively in progress" apart from "it already tried and
// failed, and nothing is retrying it right now" (see isLoadingAfterLogin/
// loginSyncFailed below).
const isSilentSyncRunning = ref(false);

export function syncAll({ silent = true } = {}) {
  if (silent) {
    if (!inFlightSilentSync) {
      isSilentSyncRunning.value = true;
      inFlightSilentSync = runSyncAll(true).finally(() => {
        inFlightSilentSync = null;
        isSilentSyncRunning.value = false;
      });
    }
    return inFlightSilentSync;
  }
  return (inFlightSilentSync ?? Promise.resolve()).then(() => runSyncAll(false));
}

// Fast, local-only half of the login transition — see login_form.vue's
// submit(), which calls this synchronously and navigates immediately
// afterward rather than waiting on loadFreshAfterLogin's network pull.
// Discarding the guest's local setups can't wait for that pull to land
// (this browser's old data must never linger even for a moment once a
// different identity is logging in), but the fresh replacement data
// doesn't need to be here *before* navigating — hasCompletedFirstSync is
// left false on purpose, so the destination page's own background sync
// (see account_widget.vue's onMounted) falls into syncAll's existing
// !hasCompletedFirstSync branch and runs loadFreshAfterLogin for real,
// same as it already does for e.g. resuming a session without a fresh
// login. The user sees one bare "Default" setup for the moment in
// between — local storage's honest answer until the real one arrives.
export function discardLocalIdentity() {
  resetSyncState();
  const { replaceAllSetups } = useSetups();
  replaceAllSetups([], null);
}

// Runs once right after an explicit login/register (see login_form.vue's
// submit()) — deliberately NOT syncAll(): nothing this browser had locally
// before authenticating (a guest's setups, or a previous account's
// leftovers on a shared machine) should be merged into, or pushed onto, the
// account just logged into. Instead this does a hard replace — pull the
// account's own setups and make that the entirety of local state, via
// setups.js's replaceAllSetups, discarding whatever was there before.
// Anyone who wants to bring a specific setup along can use the existing
// Export/Import feature (see input_setups.vue) rather than relying on an
// implicit, unrequested merge.
//
// `silent` defaults to false since the direct caller (login_form.vue) is
// an explicit action that deserves its own feedback if it fails; syncAll's
// own fallback call to this (see above, for resuming a session that never
// finished its first sync) passes its own `silent` through instead, so
// that path stays automated-quiet like everything else in syncAll.
export async function loadFreshAfterLogin(silent = false) {
  resetSyncState();

  const { isLoggedIn } = useAuth();
  if (!isLoggedIn.value) return true;

  let remoteSetups;
  try {
    ({ setups: remoteSetups } = await apiFetch('/setups'));
  } catch (err) {
    reportFailure(`Could not load your setups: ${err.message}`, silent);
    return false;
  }

  // Write each setup's data to storage before handing the new index over —
  // none of these ids can match whatever's currently "active" locally (they
  // belong to a different, just-authenticated account), so applySetupData
  // always takes its storage-write branch here, never the live-`inputs` one.
  for (const remote of remoteSetups) {
    applySetupData(remote.id, { ...remote.data, name: remote.name, updated_at: remote.updated_at, calc: remote.data.calc ?? null });
  }

  const { replaceAllSetups } = useSetups();
  replaceAllSetups(
    remoteSetups.map((s) => s.id),
    remoteSetups[0]?.id ?? null
  );

  const nowKnownState = {};
  for (const remote of remoteSetups) nowKnownState[remote.id] = remote.updated_at;
  knownServerState.value = nowKnownState;
  // Set even when remoteSetups is empty (a brand new account) — that's a
  // legitimate, fully-reconciled state too, not "still needs a first sync."
  hasCompletedFirstSync.value = true;
  lastSyncOk.value = true;
  return true;
}

// Fast, local-only, synchronous — see login_form.vue's submit(), which
// calls this and navigates immediately afterward rather than waiting on a
// network push, the same reasoning as discardLocalIdentity above. Unlike
// an ordinary login, a brand new account has no server-side setups to
// discard local state in favor of, and a guest is limited to exactly one
// local setup (see setups.js) — so there's nothing to reconcile, and
// nothing here actually needs the network: hasCompletedFirstSync is set
// true immediately (this account's local setup IS its source of truth,
// not something to pull), so the destination page's own background sync
// runs the *normal* syncAll() path, which pulls (finding nothing, since
// the account is brand new) and pushes every local setup — including this
// one — on its own, no explicit push needed here at all.
export function registerAndKeepLocal() {
  resetSyncState();

  const { isLoggedIn } = useAuth();
  if (!isLoggedIn.value) return;

  hasCompletedFirstSync.value = true;
}

// Drives the "Sync now" button's visibility (see account_widget.vue): shown
// only when there's something local worth pushing, since a remote-only
// change (made on another device) isn't something this button would fix
// anyway — pulling already happens automatically on load/reconnect, so the
// fix for "another device changed something" is just reloading the page,
// not pressing this button. True when logged in and either a delete is
// still queued, or some local setup's own updated_at is newer than what
// this browser last confirmed the server has for it (including a setup
// that's never been synced at all — not present in knownServerState).
export const hasLocalChanges = computed(() => {
  const { isLoggedIn } = useAuth();
  if (!isLoggedIn.value) return false;
  if (pendingDeletes.value.length > 0) return true;

  const { setups } = useSetups();
  return setups.value.some((setup) => {
    const knownUpdatedAt = knownServerState.value[setup.id];
    if (knownUpdatedAt == null) return true;
    const data = JSON.parse(exportSetupJson(setup.id));
    return (data.updated_at || 0) > knownUpdatedAt;
  });
});

// True for the stretch right after a fresh login/register where the account
// is authenticated, its real setups haven't been pulled down yet, and a
// pull is actively in flight — the window where the app is still showing
// discardLocalIdentity's bare bootstrap "Default" (see its own comment)
// rather than anything the user actually saved. Drives a "loading your
// setups" indicator (see login_sync_indicator.vue) so that gap reads as
// "still loading" instead of "my setup got reset to Default". Gated on
// isSilentSyncRunning, not just !hasCompletedFirstSync alone, so this turns
// off (in favor of loginSyncFailed below) once an attempt has actually
// given up rather than spinning forever.
export const isLoadingAfterLogin = computed(() => {
  const { isLoggedIn } = useAuth();
  return isLoggedIn.value && !hasCompletedFirstSync.value && isSilentSyncRunning.value;
});

// True once the post-login pull has given up (timed out, offline, a server
// error) without ever succeeding, and nothing is currently retrying it —
// the same window as isLoadingAfterLogin, but after that spinner would
// otherwise have run forever with no feedback and no way to retry short of
// an unguided page reload. Drives a "couldn't load your setups" banner with
// a manual retry (see login_sync_indicator.vue's retry(), which calls
// syncAll non-silently so this attempt's own outcome gets a toast either
// way, unlike the silent automatic ones).
export const loginSyncFailed = computed(() => {
  const { isLoggedIn } = useAuth();
  return isLoggedIn.value && !hasCompletedFirstSync.value && !isSilentSyncRunning.value;
});

export function useSync() {
  return {
    syncAll,
    resetSyncState,
    hasLocalChanges,
    loadFreshAfterLogin,
    registerAndKeepLocal,
    discardLocalIdentity,
    isLoadingAfterLogin,
    loginSyncFailed,
  };
}
